import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isAxiosError } from 'axios';
import * as yup from 'yup';

import { ACCOUNT_FIELD_CONFIGS, AccountFieldInput } from '@hcs/authn';
import {
  ActionButtons,
  Anchor,
  ButtonProps,
  FormError,
} from '@hcs/design-system';
import {
  AccountFields,
  MeaningfulEventTypes,
  SelfServePlan,
  SelfServiceRegisterFormFields,
} from '@hcs/types';
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@hcs/urls';

import {
  SelfServiceRegisterVariables,
  useSelfServiceRegister,
} from '../../hooks';
import {
  CheckSelfServeRegisterCoupon,
  CheckSelfServeRegisterCouponProps,
} from '../CheckSelfServeRegisterCoupon';
import { CheckSelfServeRegisterCouponResult } from '../CheckSelfServeRegisterCoupon/CheckSelfServeRegisterCouponResult';

import styles from './SignUp.module.css';

const yupSchema = yup.object().shape({
  [AccountFields.FirstName]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.FirstName].validator,
  [AccountFields.LastName]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.LastName].validator,
  [AccountFields.CompanyName]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.CompanyName].validator,
  [AccountFields.Email]: ACCOUNT_FIELD_CONFIGS[AccountFields.Email].validator,
  [AccountFields.Password]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.Password].validator,
});

export interface SignUpProps {
  className?: string;
  cta?: Omit<ButtonProps, 'dataHcName'>;
  navigateToLogin: VoidFunction;
  onSuccess?: VoidFunction;
  actionPortalIdRender?: string;
  confirmUrl?: string;
  plan: SelfServePlan;
  couponCode?: string;
  onCouponValid?: CheckSelfServeRegisterCouponProps['onCouponValid'];
  onCouponInvalid?: CheckSelfServeRegisterCouponProps['onCouponInvalid'];
  onCouponCleared?: VoidFunction;
  onChangePlan?: (newPlanId: string) => void;
}

const dataHcName = 'self-service-sign-up';

export const SignUpForm = ({
  className,
  navigateToLogin,
  actionPortalIdRender,
  cta,
  onSuccess,
  plan,
  confirmUrl,
  couponCode,
  onCouponValid,
  onCouponInvalid,
  onChangePlan,
  onCouponCleared,
}: SignUpProps) => {
  const form = useForm<SelfServiceRegisterFormFields>({
    resolver: yupResolver(yupSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { mutate: selfServiceMutate, isPending } = useSelfServiceRegister({
    onError(error) {
      if (isAxiosError(error)) {
        // 400 is usually password not meeting requirements or something that isn't a problem to address
        const errors = error.response?.data.errors;
        if (error.response?.status === 400 && errors) {
          if (errors?.first_name) {
            form.setError('firstName', {
              type: 'manual',
              message: errors.first_name,
            });
          }
          if (errors?.last_name) {
            form.setError('lastName', {
              type: 'manual',
              message: errors.last_name,
            });
          }
          if (errors?.email) {
            form.setError('email', {
              type: 'manual',
              message: errors.email,
            });
          }
          if (errors?.password) {
            form.setError('password', {
              type: 'manual',
              message: errors.password,
            });
          }
          setErrorMessage(
            `Sign Up failed due to invalid fields: ${error.response.data.message}`,
          );
          return;
        }
      }
      // ignoring the account api error messages as they are purposefully vague
      setErrorMessage('Sign Up Failed');
    },
    onSuccess() {
      form.reset();
      onSuccess?.();
    },
  });

  const handleSubmit = (data: SelfServiceRegisterFormFields) => {
    const payload: SelfServiceRegisterVariables = {
      first_name: data[AccountFields.FirstName],
      last_name: data[AccountFields.LastName],
      company_name: data[AccountFields.CompanyName] || undefined,
      email: data[AccountFields.Email],
      password: data[AccountFields.Password],
      accepted_terms: true,
      confirm_url: confirmUrl,
      plan,
    };
    selfServiceMutate(payload);
  };
  return (
    <div
      data-hc-event-section="Self Serve Sign Up Form"
      data-hc-name={dataHcName}
      className={className}
    >
      {errorMessage && (
        <FormError
          // Hardcoded to prevent current automation from breaking
          dataHcName={`${dataHcName}-errors`}
          value={<div>{errorMessage}</div>}
        />
      )}
      <FormProvider {...form}>
        <form
          className={styles.Form}
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <div className={styles.SectionLabel}>Your Information</div>
          <Controller
            name={AccountFields.FirstName}
            control={form.control}
            render={({ field }) => (
              <AccountFieldInput
                {...field}
                className={styles.AccountFieldInput}
                name={AccountFields.FirstName}
                error={form.formState.errors[field.name]?.message}
              />
            )}
          />
          <Controller
            name={AccountFields.LastName}
            control={form.control}
            render={({ field }) => (
              <AccountFieldInput
                {...field}
                className={styles.AccountFieldInput}
                name={AccountFields.LastName}
                error={form.formState.errors[field.name]?.message}
              />
            )}
          />
          <Controller
            name={AccountFields.Email}
            control={form.control}
            render={({ field }) => (
              <AccountFieldInput
                {...field}
                className={styles.AccountFieldInput}
                onChange={(v) => {
                  setErrorMessage(undefined);
                  field.onChange(v);
                }}
                name={AccountFields.Email}
                error={form.formState.errors[field.name]?.message}
              />
            )}
          />
          <Controller
            name={AccountFields.Password}
            control={form.control}
            render={({ field }) => (
              <AccountFieldInput
                {...field}
                className={styles.AccountFieldInput}
                name={AccountFields.Password}
                error={form.formState.errors[field.name]?.message}
              />
            )}
          />
          <div className={styles.SectionLabel}>Company Information</div>
          <Controller
            name={AccountFields.CompanyName}
            control={form.control}
            render={({ field }) => (
              <AccountFieldInput
                {...field}
                className={styles.AccountFieldInput}
                name={AccountFields.CompanyName}
                error={form.formState.errors[field.name]?.message}
              />
            )}
          />
          <div className={styles.SectionLabel}>Coupon Code</div>
          <CheckSelfServeRegisterCoupon
            planId={plan.id}
            couponCode={couponCode}
            onChange={(c) => {
              if (!c) {
                onCouponCleared?.();
              }
            }}
            onCouponValid={onCouponValid}
            onCouponInvalid={onCouponInvalid}
          />
          {couponCode && (
            <CheckSelfServeRegisterCouponResult
              planId={plan.id}
              couponCode={couponCode}
              onChangePlan={onChangePlan}
            />
          )}
          <div className={styles.AccountFieldInput}>
            By signing up for an account, you agree to our{' '}
            <Anchor
              dataHcName="terms-of-use-link"
              href={TERMS_OF_USE_URL}
              target="_blank"
              rel="noreferrer"
            >
              Terms of Use
            </Anchor>
            , consent to our{' '}
            <Anchor
              dataHcName="privary-policy"
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </Anchor>
            , and understand that your subscription will renew automatically
            until canceled.
          </div>
          <div>
            Already Have an Account?{' '}
            <Anchor
              dataHcName={`${dataHcName}-login`}
              onClick={navigateToLogin}
            >
              Login
            </Anchor>
          </div>
          <ActionButtons
            dataHcName={`${dataHcName}-actions`}
            portalIdRender={actionPortalIdRender}
            actions={[
              {
                dataHcName: `${dataHcName}-submit-button`,
                dataHcEventName: 'Self Serve Sign Up Submit',
                dataHcEventType: MeaningfulEventTypes.Goal,
                label: 'Sign Up',
                onClick: () => form.handleSubmit(handleSubmit)(),
                disabled: isPending || !form.formState.isValid,
                ...(cta || {}),
              },
            ]}
          />
        </form>
      </FormProvider>
    </div>
  );
};
